import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { 
  Square, 
  Search, 
  Users, 
  AlertTriangle,
  CheckCircle,
  Clock,
  MapPin,
  Briefcase,
  Loader2,
  UserX,
  Settings
} from 'lucide-react';
import { WorkEntry, User } from '../../types';
import { apiService } from '../../services/api';
import { WorkSettingsDialog } from './WorkSettingsDialog';
import { calculateWorkDuration, formatDuration, formatRomanianTime } from '../../utils/timeUtils';

interface ActiveWorkersManagementProps {
  workEntries: WorkEntry[];
  allUsers: User[];
  onRefresh: () => void;
}

export const ActiveWorkersManagement: React.FC<ActiveWorkersManagementProps> = ({
  workEntries,
  allUsers,
  onRefresh
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWorkers, setSelectedWorkers] = useState<number[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [locallyStoppedEntries, setLocallyStoppedEntries] = useState<Set<number>>(new Set());
  const [lastStopTime, setLastStopTime] = useState<number>(0);
  const [stopProgress, setStopProgress] = useState<string | null>(null);
  const [workSettingsDialog, setWorkSettingsDialog] = useState<{
    open: boolean;
    userId: number;
    userName: string;
  }>({ open: false, userId: 0, userName: '' });
  const { toast } = useToast();

  // Clear local state if workEntries change significantly (parent refresh)
  useEffect(() => {
    // If more than 3 seconds have passed since last stop and we have local state
    if (lastStopTime && Date.now() - lastStopTime > 3000 && locallyStoppedEntries.size > 0) {
      setLocallyStoppedEntries(new Set());
    }
  }, [workEntries, lastStopTime, locallyStoppedEntries.size]);

  // Get active work entries, excluding locally stopped ones
  const activeEntries = useMemo(() => {
    return workEntries.filter(entry => 
      !entry.endTime && 
      entry.isActive && 
      !locallyStoppedEntries.has(entry.id)
    );
  }, [workEntries, locallyStoppedEntries]);

  // Filter by search
  const filteredEntries = useMemo(() => {
    if (!searchQuery) return activeEntries;
    
    const query = searchQuery.toLowerCase();
    return activeEntries.filter(entry =>
      entry.userName.toLowerCase().includes(query) ||
      entry.projectName?.toLowerCase().includes(query) ||
      entry.locationName?.toLowerCase().includes(query)
    );
  }, [activeEntries, searchQuery]);

  // Get user details for active entries
  const activeWorkersWithDetails = useMemo(() => {
    return filteredEntries.map(entry => {
      const user = allUsers.find(u => u.id === entry.userId);
      return {
        ...entry,
        userRole: user?.role || 'worker',
        isOnBreak: entry.activeBreak?.isActive || false
      };
    });
  }, [filteredEntries, allUsers]);

  const handleSelectAll = () => {
    if (selectedWorkers.length === activeWorkersWithDetails.length) {
      setSelectedWorkers([]);
    } else {
      setSelectedWorkers(activeWorkersWithDetails.map(w => w.id));
    }
  };

  const handleSelectWorker = (workEntryId: number) => {
    setSelectedWorkers(prev =>
      prev.includes(workEntryId)
        ? prev.filter(id => id !== workEntryId)
        : [...prev, workEntryId]
    );
  };

  const handleStopSelected = async () => {
    if (selectedWorkers.length === 0) {
      toast({
        title: 'Atenție',
        description: 'Selectați cel puțin un muncitor',
        variant: 'destructive'
      });
      return;
    }
    setShowConfirmDialog(true);
  };

  const confirmStopWork = async () => {
    setIsLoading(true);
    setShowConfirmDialog(false);

    // Optimistically update UI immediately
    const stoppedEntries = new Set(locallyStoppedEntries);
    selectedWorkers.forEach(id => stoppedEntries.add(id));
    setLocallyStoppedEntries(stoppedEntries);
    setLastStopTime(Date.now());
    
    // Show progress
    const selectedEntries = activeWorkersWithDetails.filter(w => selectedWorkers.includes(w.id));
    const workerNames = selectedEntries.map(w => w.userName).join(', ');
    setStopProgress(`Se oprește munca pentru: ${workerNames}`);

    try {
      const result = await apiService.adminBulkStopWork(
        selectedWorkers,
        'Oprit de administrator'
      );

      setSelectedWorkers([]);

      if (result.successful.length > 0) {
        setStopProgress(`✅ Munca oprită pentru ${result.successful.length} muncitori`);
        toast({
          title: 'Succes',
          description: result.message,
        });
        
        // Schedule a refresh after a short delay to sync with backend
        setTimeout(() => {
          setLocallyStoppedEntries(new Set()); // Clear local state
          setStopProgress(null);
          onRefresh();
        }, 2000);
      }

      if (result.failed.length > 0) {
        // Revert optimistic updates for failed items
        const failedIds = new Set(result.failed.map(f => f.workEntryId));
        const updatedStoppedEntries = new Set(
          Array.from(locallyStoppedEntries).filter(id => !failedIds.has(id))
        );
        setLocallyStoppedEntries(updatedStoppedEntries);
        
        // Show detailed failure reasons
        const failureMessages = result.failed.map(f => `${f.userName}: ${f.reason}`).join('\n');
        toast({
          title: 'Eroare parțială',
          description: `Câteva opriri au eșuat:\n${failureMessages}`,
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error in bulk stop work:', error);
      
      // Revert all optimistic updates on error
      const revertedEntries = new Set(
        Array.from(locallyStoppedEntries).filter(id => !selectedWorkers.includes(id))
      );
      setLocallyStoppedEntries(revertedEntries);
      
      toast({
        title: 'Eroare',
        description: 'A apărut o eroare la oprirea muncii',
        variant: 'destructive'
      });
      
      // Clear progress on error
      setTimeout(() => {
        setStopProgress(null);
      }, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (dateString: string): string => {
    return formatRomanianTime(dateString);
  };

  const getWorkDuration = (startTime: string): string => {
    const hours = calculateWorkDuration(startTime);
    return formatDuration(hours);
  };

  if (activeEntries.length === 0) {
    return null;
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Gestionare Muncitori Activi ({activeWorkersWithDetails.length})
            </CardTitle>
            <Button
              onClick={handleStopSelected}
              disabled={selectedWorkers.length === 0 || isLoading}
              variant="destructive"
              className="flex items-center gap-2"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Square className="h-4 w-4" />
              )}
              Oprește Selectați ({selectedWorkers.length})
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Progress indicator */}
          {stopProgress && (
            <Alert className="border-blue-200 bg-blue-50">
              <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
              <AlertDescription className="text-blue-800 font-medium">
                {stopProgress}
              </AlertDescription>
            </Alert>
          )}
          
          {/* Search and Select All */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Caută după nume, proiect sau locație..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAll}
              className="flex items-center gap-2"
            >
              <Checkbox
                checked={selectedWorkers.length === activeWorkersWithDetails.length && activeWorkersWithDetails.length > 0}
                className="pointer-events-none"
              />
              Selectează toți
            </Button>
          </div>

          {/* Active Workers List */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {activeWorkersWithDetails.map(entry => {
              const isBeingStopped = locallyStoppedEntries.has(entry.id);
              return (
                <div
                  key={entry.id}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-all duration-300 ${
                    isBeingStopped 
                      ? 'bg-red-50 border-red-300 opacity-60 animate-pulse' 
                      : selectedWorkers.includes(entry.id) 
                        ? 'bg-blue-50 border-blue-300' 
                        : 'bg-gray-50'
                  }`}
                >
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={selectedWorkers.includes(entry.id)}
                    onCheckedChange={() => handleSelectWorker(entry.id)}
                    disabled={isBeingStopped}
                  />
                  
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{entry.userName}</p>
                      <Badge variant="secondary" className="text-xs">
                        {entry.userRole === 'supervisor' ? 'Supervisor' : 'Muncitor'}
                      </Badge>
                      {entry.isOnBreak && (
                        <Badge variant="warning" className="text-xs">
                          În pauză
                        </Badge>
                      )}
                      {isBeingStopped && (
                        <Badge variant="destructive" className="text-xs animate-pulse">
                          Se oprește...
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                      <span className="flex items-center gap-1">
                        <Briefcase className="h-3 w-3" />
                        {entry.projectName || 'Proiect nedefinit'}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {entry.locationName || 'Locație nedefinită'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Din {formatTime(entry.startTime)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setWorkSettingsDialog({
                      open: true,
                      userId: entry.userId,
                      userName: entry.userName
                    })}
                    disabled={isBeingStopped}
                    title="Setează Pauză"
                  >
                    <Settings className="h-4 w-4" />
                    Setează Pauză
                  </Button>
                  <div className="text-right">
                    <p className="font-medium text-sm">{getWorkDuration(entry.startTime)}</p>
                    <p className="text-xs text-gray-500">Timp lucrat</p>
                  </div>
                </div>
              </div>
            );
          })}
          </div>

          {activeWorkersWithDetails.length === 0 && searchQuery && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Nu s-au găsit muncitori activi pentru căutarea "{searchQuery}"
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserX className="h-5 w-5 text-red-600" />
              Confirmare Oprire Muncă
            </DialogTitle>
            <DialogDescription className="space-y-3">
              <p>
                Sunteți sigur că doriți să opriți munca pentru {selectedWorkers.length} 
                {selectedWorkers.length === 1 ? ' muncitor' : ' muncitori'}?
              </p>
              <Alert className="border-orange-200 bg-orange-50">
                <AlertTriangle className="h-4 w-4 text-orange-600" />
                <AlertDescription className="text-orange-800">
                  Această acțiune va încheia sesiunea de lucru actuală pentru muncitorii selectați.
                </AlertDescription>
              </Alert>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              disabled={isLoading}
            >
              Anulează
            </Button>
            <Button
              variant="destructive"
              onClick={confirmStopWork}
              disabled={isLoading}
              className="flex items-center gap-2"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Square className="h-4 w-4" />
              )}
              Oprește Munca
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Work Settings Dialog */}
      <WorkSettingsDialog
        open={workSettingsDialog.open}
        onOpenChange={(open) => setWorkSettingsDialog(prev => ({ ...prev, open }))}
        userId={workSettingsDialog.userId}
        userName={workSettingsDialog.userName}
        onSettingsUpdated={onRefresh}
      />
    </>
  );
};